import Card from "@mui/material/Card";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";

import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import LoadingButton from "@mui/lab/LoadingButton";
import { MinPasswordLength } from "../../constants/main";
import { FetchStatusEnum, FetchStatusStrings } from "../../enums/fetchStatusEnum";
import i18nStore from "../../i18n/store";
import { NewPasswordRequestSchema } from "../../api/schemas/requests/auth";
import { API } from "../../api/api";
import PasswordInput from "../../components/inputs/PasswordInput";
import ErrorMsg from "../../ui/ErrorMsg";
import { successResetPasswordPath } from "../../routes/paths";


const NewPasswordForm = observer(() => {
    const [searchParams, setSearchParams] = useSearchParams();
    const userId = searchParams.get("user_id") || 0;
    const lang = searchParams.get("lang") || "en";
    const secret = searchParams.get("secret") || "";

    const translations = i18nStore.getTranslations;
    const navigate = useNavigate();

    const [password, setPassword] = useState<string | undefined>();
    const [password2, setPassword2] = useState<string | undefined>();

    const [isPasswordsMatch, setIsPasswordsMatch] = useState<boolean | undefined>();
    const [isPasswordHasGoodLen, setIsPasswordHasGoodLen] = useState<boolean | undefined>();
    const [isPasswordsOK, setIsPasswordsOK] = useState<boolean | undefined>();

    const [isFormDisabled, setIsFormDisabled] = useState(true);

    const [errorText, setErrorText] = useState<string>("Error");
    const [actionStatus, setActionStatus] = useState<FetchStatusStrings>(FetchStatusEnum.IDLE);

    useEffect(() => {
        setActionStatus(FetchStatusEnum.IDLE);
    }, [password, password2]);

    useEffect(() => {
        // Если пароли еще не изменялись
        if (password === undefined && password2 === undefined) return;

        // Если пароль короткий
        if (password && password.length < MinPasswordLength) {
            setIsPasswordHasGoodLen(false);
            return;
        }
        setIsPasswordHasGoodLen(true);

        // пользователь еще не начал вводить второй пароль
        if (password2 === undefined) return;

        // Если пароли не совпадают
        if (password !== password2) {
            setIsPasswordsMatch(false);
            return;
        }
        setIsPasswordsMatch(true);
    }, [password, password2]);

    useEffect(() => {
        if (isPasswordHasGoodLen === undefined || isPasswordsMatch === undefined) {
            setIsPasswordsOK(undefined);
        }
        setIsPasswordsOK(isPasswordHasGoodLen && isPasswordsMatch);
    }, [isPasswordsMatch, isPasswordHasGoodLen]);

    useEffect(() => {
        setIsFormDisabled(!isPasswordsOK);
    }, [isPasswordsOK]);

    function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (isFormDisabled) return;
        if (!password) return;

        setActionStatus(FetchStatusEnum.FETCHING);

        const requestData: NewPasswordRequestSchema = {
            password: password,
        };

        if (!secret || !userId) {
            alert("Bad data!");
            return;
        }
        API.auth
            .setNewPassword(
                requestData,
                secret,
                userId,
                lang,
            )
            .then(() => {
                setPassword("");
                setActionStatus(FetchStatusEnum.SUCCESS);
                navigate(successResetPasswordPath);
            })
            .catch((err) => {
                setActionStatus(FetchStatusEnum.ERROR);

                const statusCode = err.status || err.response?.status || 500;
                if (statusCode >= 500) {
                    setErrorText(translations.errServiceUnavailableMsg);
                } else if (statusCode >= 400) {
                    setErrorText(translations.errEmailIsBusyMsg);
                }
            });
    }

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Card
                sx={{
                    p: 3,
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography component="h1" variant="h5">
                    {translations.newPassword}
                </Typography>
                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
                    <Grid container>

                        <Box sx={{ mt: 2, width: "100%" }}>
                            <PasswordInput password={password || ""} setPassword={setPassword} />
                        </Box>
                        <Box sx={{ mt: 2, width: "100%" }}>
                            <PasswordInput
                                password={password2 || ""}
                                setPassword={setPassword2}
                                label={translations.repeatPassword}
                            />
                        </Box>

                        <ErrorMsg
                            errText={translations.errPasswordsDontMatchMsg}
                            visible={isPasswordsMatch !== undefined && !isPasswordsMatch}
                        />
                        <ErrorMsg
                            errText={translations.errPasswordsTooShortMsg}
                            visible={isPasswordHasGoodLen !== undefined && !isPasswordHasGoodLen}
                        />

                        <ErrorMsg errText={errorText} visible={actionStatus === FetchStatusEnum.ERROR} />
                    </Grid>

                    <LoadingButton
                        disabled={isFormDisabled}
                        loading={actionStatus === FetchStatusEnum.FETCHING}
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                    >
                        {translations.save}
                    </LoadingButton>

                </Box>
            </Card>
        </Container>
    );
});


export default NewPasswordForm;
